import { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { api, apiPaths, clearCsrfToken, registerSessionExpiredHandler, clearAllAuthStorage, resetAuthRefreshState } from '../config/api';
import { requestDeduplicator } from '../utils/requestDeduplicator';

const AuthContext = createContext();

// Cache configuration
const AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const AUTH_STALE_DURATION = 10 * 60 * 1000; // 10 minutes
const AUTH_CACHE_KEY = 'auth_cache';
const ADMIN_LOGOUT_INTENT_KEY = 'admin_logout_intent';
const ADMIN_LOGOUT_TS_KEY = 'admin_logout_ts';

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const cacheTimestamp = useRef(null);
  const isFetching = useRef(false);
  const backgroundRefreshTimer = useRef(null);

  const sessionExpiredRef = useRef(false);

  const getCachedAuth = useCallback(() => {
    try {
      const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
      if (!cached) return { data: null, isStale: false };

      const { userData, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      // Hard expired (> 10 min)
      if (age > AUTH_STALE_DURATION) {
        sessionStorage.removeItem(AUTH_CACHE_KEY);
        return { data: null, isStale: false };
      }

      // Stale but usable (> 5 min but < 10 min)
      const isStale = age > AUTH_CACHE_DURATION;

      return { data: userData, isStale };
    } catch {
      return { data: null, isStale: false };
    }
  }, []);

  // Set cached auth data
  const setCachedAuth = useCallback((userData) => {
    try {
      const cacheData = {
        userData,
        timestamp: Date.now()
      };
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cacheData));
      cacheTimestamp.current = Date.now();
    } catch (error) {
      // cache write failed — non-critical
    }
  }, []);

  // Clear cached auth data
  const clearAuthCache = useCallback(() => {
    try {
      sessionStorage.removeItem(AUTH_CACHE_KEY);
      cacheTimestamp.current = null;
    } catch (error) {
      // cache clear failed — non-critical
    }
  }, []);

  const performSessionExpired = useCallback(async () => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;

    setUser(null);
    clearAuthCache();
    clearAllAuthStorage();
    requestDeduplicator.clearResultCache('/admin');
    if (backgroundRefreshTimer.current) {
      clearTimeout(backgroundRefreshTimer.current);
    }
    window.dispatchEvent(new Event('authStateChanged'));

    try {
      await api.post(apiPaths.auth.logout(), {}, {
        headers: { 'Content-Type': 'application/json' },
        _skipAuthRefresh: true,
      });
    } catch {
      // Best-effort cookie clear
    }

    const isAdminPath = window.location.pathname.startsWith('/admin');
    window.location.href = isAdminPath ? '/admin/login' : '/login';
  }, [clearAuthCache]);

  // Background refresh without loading state
  const backgroundRefreshAuth = useCallback(async () => {
    if (isFetching.current) return;

    isFetching.current = true;
    try {
      const response = await api.get(apiPaths.auth.me());
      const userData = response.data;
      setUser(userData);
      setCachedAuth(userData);
      scheduleBackgroundRefresh();
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403 || status === 400 || status === 500) {
        setUser(null);
        clearAuthCache();
      }
    } finally {
      isFetching.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCachedAuth, clearAuthCache]);

  // Schedule proactive refresh
  const scheduleBackgroundRefresh = useCallback(() => {
    if (backgroundRefreshTimer.current) {
      clearTimeout(backgroundRefreshTimer.current);
    }

    // Refresh 30 seconds before expiry (4:30 minutes)
    backgroundRefreshTimer.current = setTimeout(() => {
      backgroundRefreshAuth();
    }, AUTH_CACHE_DURATION - 30000);
  }, [backgroundRefreshAuth]);

  // Check authentication with stale-while-revalidate
  const checkAuth = useCallback(async (forceRefresh = false) => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const { data: cachedUser } = getCachedAuth();

      if (cachedUser) {
        setUser(cachedUser);
        setLoading(false);
        // Always verify with server — stale cache must not keep invalid sessions visible
        backgroundRefreshAuth();
        return;
      }
    }

    // Prevent duplicate requests
    if (isFetching.current) return;
    isFetching.current = true;

    try {
      const response = await api.get(apiPaths.auth.me());
      const userData = response.data;
      setUser(userData);
      setCachedAuth(userData);
      scheduleBackgroundRefresh();
    } catch (error) {
      setUser(null);
      clearAuthCache();
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [getCachedAuth, setCachedAuth, clearAuthCache, backgroundRefreshAuth, scheduleBackgroundRefresh]);

  // Initial auth check
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    registerSessionExpiredHandler(performSessionExpired);
    return () => registerSessionExpiredHandler(null);
  }, [performSessionExpired]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backgroundRefreshTimer.current) {
        clearTimeout(backgroundRefreshTimer.current);
      }
    };
  }, []);

  // Called by login page after successful /api/auth/login
  const login = (userData /* token is intentionally ignored */) => {
    sessionExpiredRef.current = false;
    resetAuthRefreshState();
    try {
      sessionStorage.removeItem(ADMIN_LOGOUT_INTENT_KEY);
      sessionStorage.removeItem(ADMIN_LOGOUT_TS_KEY);
    } catch {}
    // Clear stale (anonymous) CSRF token so the next mutation fetches a user-bound one.
    clearCsrfToken();
    setUser(userData);
    setCachedAuth(userData);
    scheduleBackgroundRefresh();
    window.dispatchEvent(new Event('authStateChanged'));
  };

  const logout = useCallback(async () => {
    try {
      sessionStorage.setItem(ADMIN_LOGOUT_INTENT_KEY, '1');
      sessionStorage.setItem(ADMIN_LOGOUT_TS_KEY, String(Date.now()));
    } catch {}
    sessionExpiredRef.current = false;
    setUser(null);
    clearAuthCache();
    clearAllAuthStorage();
    requestDeduplicator.clearResultCache('/admin');
    if (backgroundRefreshTimer.current) {
      clearTimeout(backgroundRefreshTimer.current);
    }
    window.dispatchEvent(new Event('authStateChanged'));

    try {
      await api.post(apiPaths.auth.logout(), {}, {
        headers: { 'Content-Type': 'application/json' },
        _skipAuthRefresh: true,
      });
    } catch {
      // State is already cleared above — backend call is best-effort
    }
  }, [clearAuthCache]);

  const updateUser = (updatedData) => {
    setUser(prev => {
      const updated = { ...prev, ...updatedData };
      setCachedAuth(updated);
      return updated;
    });
  };

  const refreshUser = useCallback(() => {
    checkAuth(true);
  }, [checkAuth]);

  const value = {
    user,
    login,
    logout,
    updateUser,
    refreshUser,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };